import FileDownloaderButton from 'components/FileDownloaderButton';
import IconButton from 'components/IconButton';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  showFailureToast,
  showSuccessToast,
  showWarningToast,
} from 'lib/ToastHelper';
import React, { useCallback, useEffect } from 'react';
import { Badge } from 'reactstrap';
import { companyIdAtom } from 'views/state';

import ResetDashboardButton from './ResetDashboardButton';
import {
  assetDataAtom,
  forceRerenderAssetGridAtom,
  selectedRowIdsAtom,
  findingsCountForCheckedHostsAtom,
  policyIdFilterAtom as assetsPolicyIdFilterAtom,
  assetDataQueryParamsAtom,
  checkedHostsFindingsMapAtom,
} from './state';
import {
  appliedHostIdsAtom,
  pcRefreshAtom,
  policyIdFilterAtom,
} from '../state';

// TODO: update the applied row background color to indicate the applied rows

const BulkActions: React.FC = () => {
  const companyId = useAtomValue(companyIdAtom);
  const assetDataQueryParams = useAtomValue(assetDataQueryParamsAtom);
  const listingData = useAtomValue(assetDataAtom);

  const [selectedRowIds, setSelectedRowIds] = useAtom(selectedRowIdsAtom);
  const [findingsMap, setFindingsMap] = useAtom(checkedHostsFindingsMapAtom);
  const [appliedHostIds, setAppliedHostIds] = useAtom(appliedHostIdsAtom);
  const forceRerenderUI = useSetAtom(forceRerenderAssetGridAtom);
  const pcRefresh = useSetAtom(pcRefreshAtom);
  const findingsCountForCheckedHosts = useAtomValue(
    findingsCountForCheckedHostsAtom,
  );

  const assetsPolicyId = useAtomValue(assetsPolicyIdFilterAtom);
  // dashboard selected policy filter
  const setDashboardPolicyId = useSetAtom(policyIdFilterAtom);

  const count = selectedRowIds.length;

  const selectionChanged = (): boolean => {
    return appliedHostIds.sort().join(',') !== selectedRowIds.sort().join(',');
  };

  const markAppliedRows = useCallback(
    (rowIds) => {
      if (listingData) {
        for (let i = 0; i < listingData.results?.length; i++) {
          const row = listingData.results[i];
          row['isAppliedForDashboard'] = false;
        }
        const effectedRows = listingData?.results?.filter((row) =>
          rowIds.includes(row.host_id),
        );
        if (effectedRows?.length > 0) {
          effectedRows.forEach((row) => {
            row['isAppliedForDashboard'] = true;
          });
        }
        forceRerenderUI(Math.random().toString());
      }
    },
    [forceRerenderUI, listingData],
  );

  // Effect when drilldown from the CA Detail Modal
  useEffect(() => {
    const url = new URL(window.location.href);
    const hostIdParam = url.searchParams.get('host_id');
    if (hostIdParam && listingData && listingData.results?.length) {
      markAppliedRows([hostIdParam]);
      setAppliedHostIds([hostIdParam]);
      pcRefresh(Math.random().toString());
    }
  }, [listingData, setAppliedHostIds]);

  useEffect(() => {
    // This effect runs whenever listing data changes (including pagination)
    if (listingData?.results) {
      // Always apply the highlighting if there are applied host IDs
      if (appliedHostIds.length > 0) {
        // Use setTimeout to ensure this runs after the data grid has updated
        setTimeout(() => {
          markAppliedRows(appliedHostIds);
        }, 0);
      }
    }
  }, [listingData, markAppliedRows, appliedHostIds]);

  useEffect(() => {
    if (listingData?.results?.length) {
      const newFindingsMap = { ...findingsMap };

      // Remove vulnerability counts for unchecked hosts
      Object.keys(newFindingsMap).forEach((hostId: any) => {
        if (!selectedRowIds.includes(hostId)) {
          delete newFindingsMap[hostId];
        }
      });

      // Add vulnerability counts for newly checked hosts
      listingData.results.forEach((host) => {
        if (selectedRowIds.includes(host.host_id)) {
          newFindingsMap[host.host_id] = host.passed_count + host.failed_count;
        }
      });

      setFindingsMap(newFindingsMap);
    }
  }, [selectedRowIds, listingData, setFindingsMap]);

  const onUpdateDashboardClick = () => {
    // check if the vulnerability count for any of the selected host is greater then zero
    // This is to avoid plotting the dashboard graphs for all selected hosts with zero vulnerability count
    // for details check: https://securepoint360.atlassian.net/browse/SB-1722, https://securepoint360.atlassian.net/browse/SB-1723
    if (findingsCountForCheckedHosts > 0 || selectedRowIds?.length === 0) {
      markAppliedRows(selectedRowIds);
      setAppliedHostIds(selectedRowIds);
      // update dashboard selected policy filter, to match the policy filter for assets listing
      setDashboardPolicyId(assetsPolicyId);
    } else {
      showWarningToast(
        'Dashboard can not be applied, as for the selected hosts, no findings data is available. Please try a different combination of hosts.',
      );
    }
  };

  const appendParamsToUrl = (url, params) => {
    const urlObj = new URL(url, window.location.href);
    const existingParams = new URLSearchParams(urlObj.search);
    const newParams = new URLSearchParams();

    for (const [key, value] of Object.entries(params)) {
      if (!existingParams.has(key)) {
        newParams.set(key, value as string);
      }
    }

    const queryString = newParams.toString();
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}${queryString ? queryString : ''}`;
  };

  return (
    <div className="col-auto text-right align-baseline">
      {count > 0 && (
        <>
          <span className="mr-1" style={{ fontSize: '1.2em' }}>
            <Badge color="primary" pill className="mx-1">
              {count}
            </Badge>
            Selected
          </span>
          <IconButton
            id="clearSelectionButton"
            iconClass="fas fa-times"
            tooltip="Clear all selections"
            onClick={() => setSelectedRowIds([])}
          />
          <span
            className="mx-2"
            style={{
              fontSize: '24px',
              color: 'gray',
              verticalAlign: 'middle',
            }}
          >
            &#124;
          </span>
        </>
      )}
      <IconButton
        id="updateButton"
        iconClass="fa fa-chart-line fa-lg"
        tooltip="Update Dashboard For Selected Host Assets"
        disabled={
          !selectionChanged() ||
          (selectedRowIds.length === 0 && appliedHostIds.length === 0)
        }
        active={appliedHostIds.length > 0}
        stale={selectionChanged()}
        onClick={onUpdateDashboardClick}
      />
      <ResetDashboardButton />
      <FileDownloaderButton
        buttonId="vuln_export_all"
        iconClass="fa fa-file-export"
        tooltip={
          <span>
            Export assets data.
            <br /> All current filters and row selection will be applied while
            exporting data.
          </span>
        }
        fileName={`${localStorage
          .getItem('currentCompany')
          ?.toLocaleLowerCase()}_pc_assets.csv`}
        method={count > 0 ? 'POST' : 'GET'}
        downloadUrl={
          companyId
            ? appendParamsToUrl('pcm/assets/export-csv/', assetDataQueryParams)
            : ''
        }
        className="float-right"
        urlParams={{}}
        data={count > 0 ? { host_ids: selectedRowIds } : null}
        onSuccessCallback={() =>
          showSuccessToast('Success! File download completed.')
        }
        onFailureCallback={(message) => showFailureToast('Error! ' + message)}
      />
    </div>
  );
};

export default BulkActions;
